// src/pages/Instrument_Runs/components/RunActionRow/RunActionRow.tsx
import { smIconSize } from '@/constants';
import type { InstrumentRunPublic } from '@/types';
import { Breadcrumbs } from '@nautilusbio/nb-design-system/Breadcrumbs';
import { Button } from '@nautilusbio/nb-design-system/Button';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Link } from '@tanstack/react-router';
import { CloudUpload, PencilLine, Save, X } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import styles from '../../styles.module.css';

type RunActionRowProps = {
  run: InstrumentRunPublic;
  isEditing: boolean;
  onEdit: () => void;
  onCancel: () => void;
  onSave: () => void;
  onSubmit?: () => void;
};

export default function RunActionRow({ run, isEditing, onEdit, onCancel, onSave, onSubmit }: RunActionRowProps) {
  const { t } = useTranslation('runs', { keyPrefix: 'header' });
  const { t: tGeneral } = useTranslation('general');

  return (
    <Stack
      alignItems="center"
      className={styles.headerActionRow}
      fullWidth
      justifyContent="space-between"
      orientation="horizontal"
    >
      <Breadcrumbs
        links={[
          { id: 'runs', children: t('runs_label'), to: '/instrument_runs' },
          { id: 'run', children: run.name },
        ]}
        renderLink={(link, _index, defaultLinkProps) => {
          if (!link?.to) return link?.children;

          return (
            <Link className={defaultLinkProps.className} to={link.to as '/instrument_runs'}>
              {link.children}
            </Link>
          );
        }}
        separator="arrow"
        size="md"
      />

      <Stack alignItems="center" fullHeight gap={4} orientation="horizontal">
        {isEditing ? (
          <>
            <Button data-testid="run-action-cancel" onPress={onCancel} visual="unstyled">
              <X size={smIconSize} />
              {tGeneral('cancel')}
            </Button>

            {run.status === 'draft' && onSubmit && (
              <Button data-testid="run-action-submit" onPress={onSubmit} visual="unstyled">
                <CloudUpload size={smIconSize} />
                {tGeneral('submit', { defaultValue: 'Submit' })}
              </Button>
            )}

            <Button data-testid="run-action-save" onPress={onSave} visual="unstyled">
              <Save size={smIconSize} />
              {tGeneral('save')}
            </Button>
          </>
        ) : (
          <Button data-testid="edit-instrument-run" onPress={onEdit} visual="unstyled">
            <PencilLine size={smIconSize} />
            {t('edit')}
          </Button>
        )}
      </Stack>
    </Stack>
  );
}
